import { PushpinOutlined } from '@ant-design/icons';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ProviderIcon } from 'components/Custom/ProviderIcon';
import type { Organisation } from 'store/organisation/models';
import { useOverlayBackHandler } from 'utils/useOverlayBackHandler';

export type PwaCaptureEntityPickerProps = {
  open: boolean;
  entities: Organisation[];
  pinnedEntityIds?: string[];
  onClose: () => void;
  onSelect: (organisationId: string) => void;
};

export const PwaCaptureEntityPicker: React.FC<PwaCaptureEntityPickerProps> = ({
  open,
  entities,
  pinnedEntityIds = [],
  onClose,
  onSelect,
}) => {
  const { t } = useTranslation();

  useOverlayBackHandler(open, onClose, 'pwa-capture-entity-picker', true);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="scope-pwa-capture-picker"
      role="dialog"
      aria-modal="true"
      aria-labelledby="scope-pwa-capture-picker-title"
    >
      <button
        type="button"
        className="scope-pwa-capture-picker__backdrop"
        aria-label={t('common.close')}
        onClick={onClose}
      />
      <div className="scope-pwa-capture-picker__sheet">
        <div className="scope-pwa-capture-picker__header">
          <h2
            id="scope-pwa-capture-picker-title"
            className="scope-pwa-capture-picker__title"
          >
            {t('organisation.pwa-capture-select-entity-title')}
          </h2>
          <p className="scope-pwa-capture-picker__subtitle">
            {t('organisation.pwa-capture-select-entity-subtitle')}
          </p>
          <p className="scope-pwa-capture-picker__helper">
            {t('organisation.pwa-capture-select-entity-helper')}
          </p>
        </div>
        <ul className="scope-pwa-capture-picker__list">
          {entities.map((entity) => {
            const label = entity.friendlyName || entity.legalName;
            const isPinned = pinnedEntityIds.includes(entity.uuid);
            return (
              <li key={entity.uuid}>
                <button
                  type="button"
                  className={`scope-pwa-capture-picker__option${
                    isPinned ? ' scope-pwa-capture-picker__option--pinned' : ''
                  }`}
                  onClick={() => onSelect(entity.uuid)}
                >
                  <span className="scope-pwa-capture-picker__option-main">
                    {isPinned ? (
                      <PushpinOutlined
                        className="scope-pwa-capture-picker__option-pin"
                        aria-hidden
                      />
                    ) : null}
                    <span className="scope-pwa-capture-picker__option-name">
                      {label}
                    </span>
                  </span>
                  {entity.providerName ? (
                    <span className="scope-pwa-capture-picker__option-provider">
                      <ProviderIcon
                        className="scope-pwa-capture-picker__option-provider-icon"
                        providerName={entity.providerName}
                        alt=""
                        width={18}
                        height={18}
                      />
                      <span>{entity.providerName}</span>
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default PwaCaptureEntityPicker;
